import { cubaDayKey } from '../time/cubaTime';
import { normalizeTriple } from '../lottery/resultConsensus';
import { resolveOfficialReading } from '../lottery/officialDrawFormat';
import { type PlayTurn, type PlayWindow } from './playTurns';

export interface ResultLike {
  id?: string;
  dayKey?: string;
  window?: string;
  first?: string;
  second?: string;
  third?: string;
  source?: string;
  feed?: string;
  /** Lottery snapshot — field names follow the house source style. */
  picks?: {
    pick2?: string | null;
    pick3?: string | null;
    pick4?: string | null;
    pick5?: string | null;
    numbers?: string | null;
    win4?: string | null;
    cash3?: string | null;
    cash4?: string | null;
  };
  publishedAt?: number | { toMillis?: () => number; seconds?: number };
  houseId?: string;
  /** Florida Lottery YouTube upload when the platform draw has one. */
  youtubeVideoId?: string | null;
}

export interface DrawSessionLike {
  id?: string;
  dayKey?: string;
  window?: string;
  status?: string;
  first?: string;
  second?: string;
  third?: string;
  source?: string;
  feed?: string;
  settledAt?: number | { toMillis?: () => number; seconds?: number };
  updatedAt?: number | { toMillis?: () => number; seconds?: number };
  houseId?: string;
}

export interface OfficialDrawResult {
  dayKey: string;
  window: PlayWindow;
  first: string;
  second: string;
  third: string;
  source?: string;
  feed?: string;
  pick3?: string | null;
  pick4?: string | null;
  publishedAt?: number;
  id?: string;
  houseId?: string;
  youtubeVideoId?: string | null;
  status: 'confirmed';
}

export function resolvePublishedAtMs(
  raw: ResultLike['publishedAt'] | DrawSessionLike['updatedAt'],
): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw?.toMillis === 'function') return raw.toMillis();
  if (typeof raw?.seconds === 'number') return raw.seconds * 1000;
  return null;
}

export function turnKey(turn: PlayTurn): string {
  return `${turn.dayKey}:${turn.window}`;
}

export function compareTurnsNewestFirst(a: PlayTurn, b: PlayTurn): number {
  if (a.dayKey !== b.dayKey) return a.dayKey < b.dayKey ? 1 : -1;
  if (a.window === b.window) return 0;
  return a.window === 'night' ? -1 : 1;
}

/** Infer turn from stamped fields, falling back to publishedAt day. */
export function resultTurnOf(
  result: ResultLike,
  nowUtc: Date = new Date(),
): PlayTurn | null {
  const window = result.window === 'night' ? 'night' : result.window === 'day' ? 'day' : null;
  if (!window) return null;
  const dayKey = result.dayKey
    || (() => {
      const ms = resolvePublishedAtMs(result.publishedAt);
      return ms != null ? cubaDayKey(new Date(ms)) : cubaDayKey(nowUtc);
    })();
  return { dayKey, window };
}

function toOfficial(
  dayKey: string,
  window: PlayWindow,
  row: Pick<ResultLike, 'first' | 'second' | 'third' | 'source' | 'feed' | 'picks' | 'id' | 'houseId' | 'publishedAt' | 'youtubeVideoId'>,
): OfficialDrawResult | null {
  const triple = normalizeTriple(
    String(row.first ?? ''),
    String(row.second ?? ''),
    String(row.third ?? ''),
  );
  if (!triple) return null;
  const resolved = resolveOfficialReading(row.source, {
    first: triple[0],
    second: triple[1],
    third: triple[2],
    pick3: row.picks?.pick3 != null ? String(row.picks.pick3) : null,
    pick4: row.picks?.pick4 != null ? String(row.picks.pick4) : null,
    numbers: row.picks?.numbers != null ? String(row.picks.numbers) : null,
    win4: row.picks?.win4 != null ? String(row.picks.win4) : null,
    cash3: row.picks?.cash3 != null ? String(row.picks.cash3) : null,
    cash4: row.picks?.cash4 != null ? String(row.picks.cash4) : null,
  });
  return {
    dayKey,
    window,
    first: triple[0],
    second: triple[1],
    third: triple[2],
    source: row.source,
    feed: row.feed,
    pick3: resolved.pick3,
    pick4: resolved.pick4,
    id: row.id,
    houseId: row.houseId,
    publishedAt: resolvePublishedAtMs(row.publishedAt) ?? undefined,
    youtubeVideoId: row.youtubeVideoId ?? null,
    status: 'confirmed',
  };
}

/**
 * One official triple per turn: prefer confirmed/settled drawSessions,
 * else latest submission per dayKey+window.
 */
export function officialResultsForHouse(
  results: ResultLike[],
  drawSessions: DrawSessionLike[] = [],
  nowUtc: Date = new Date(),
): OfficialDrawResult[] {
  const byTurn = new Map<string, OfficialDrawResult>();
  const picksByTurn = new Map<string, { picks: NonNullable<ResultLike['picks']>; ms: number }>();

  for (const result of results) {
    const turn = resultTurnOf(result, nowUtc);
    if (!turn || !result.picks) continue;
    const key = turnKey(turn);
    const ms = resolvePublishedAtMs(result.publishedAt) ?? 0;
    const prev = picksByTurn.get(key);
    if (!prev || ms >= prev.ms) picksByTurn.set(key, { picks: result.picks, ms });
  }

  for (const session of drawSessions) {
    const status = String(session.status ?? '');
    if (status !== 'confirmed' && !session.settledAt) continue;
    const window = session.window === 'night' ? 'night' : session.window === 'day' ? 'day' : null;
    const dayKey = session.dayKey;
    if (!window || !dayKey) continue;
    const key = turnKey({ dayKey, window });
    const official = toOfficial(dayKey, window, {
      ...session,
      picks: picksByTurn.get(key)?.picks,
      publishedAt: session.settledAt ?? session.updatedAt,
      id: session.id ?? `${dayKey}_${window}`,
    });
    if (official) byTurn.set(key, official);
  }

  const latestSubmission = new Map<string, ResultLike>();
  for (const result of results) {
    const turn = resultTurnOf(result, nowUtc);
    if (!turn) continue;
    const key = turnKey(turn);
    if (byTurn.has(key)) continue;
    const prev = latestSubmission.get(key);
    const prevMs = resolvePublishedAtMs(prev?.publishedAt) ?? 0;
    const nextMs = resolvePublishedAtMs(result.publishedAt) ?? 0;
    if (!prev || nextMs >= prevMs) {
      latestSubmission.set(key, { ...result, dayKey: turn.dayKey, window: turn.window });
    }
  }

  for (const [, result] of latestSubmission) {
    const turn = resultTurnOf(result, nowUtc);
    if (!turn) continue;
    const key = turnKey(turn);
    if (byTurn.has(key)) continue;
    const official = toOfficial(turn.dayKey, turn.window, result);
    if (official) byTurn.set(key, official);
  }

  return [...byTurn.values()].sort((a, b) => compareTurnsNewestFirst(a, b));
}
